/**
 * React Query Client Configuration
 * 
 * Configures the query client with persistence for offline support.
 * 
 * @module lib/queryClient
 */

import { QueryClient } from '@tanstack/react-query';
import { clearWidgetSnapshots } from './widgetSnapshot';
import { QUERY_CACHE_STORAGE_KEY, queryPersister } from './queryPersister';
import { SESSION_MISSING_CODE } from '@/services/supabase/sessionGatedFetch';

/** The session gate's local 401: no session exists, so an immediate retry cannot succeed. */
function isSessionMissing(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === SESSION_MISSING_CODE
  );
}

/**
 * Query client instance with optimized defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus - reduces unnecessary requests
      refetchOnWindowFocus: false,
      
      // Retry once on failure — except a missing session: the gate already
      // waited for one, and AuthProvider refetches errored queries on
      // TOKEN_REFRESHED / SIGNED_IN. (Equivalent to `retry: 1` otherwise.)
      retry: (failureCount: number, error: unknown) => !isSessionMissing(error) && failureCount < 1,
      
      // Data considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      
      // Keep data in cache for 24 hours (matches persistence maxAge)
      gcTime: 24 * 60 * 60 * 1000,
      
      // Network mode for offline support
      // 'always' runs queries even when offline, using cache
      networkMode: 'always',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      
      // Allow mutations to be queued when offline
      networkMode: 'always',
    },
  },
});

/**
 * Clear all cached data (useful for logout)
 */
export function clearQueryCache(): void {
  queryClient.clear();
  
  // Also clear persisted data
  try {
    // Through the persister so its change detection resets too: otherwise the
    // next persist could be skipped as "unchanged" and the blob stay missing.
    void queryPersister.removeClient();
    // Raw removal kept: removeClient is Promisable by contract, and logout
    // must not depend on it having settled.
    localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
    clearWidgetSnapshots();
  } catch (e) {
    console.warn('Failed to clear persisted query cache:', e);
  }
}
